// src/v2/leases/LeasesTimelineView.jsx
//
// LEASES-TIMELINE-REDESIGN — Grouped accordion timeline view for the Leases page.
// Replaces the old Classic (LeasesView embed) and Action views entirely.
//
// Canonical action bands plus the separate Rolling / MTM and Expired lanes
// (assigned upstream; decision thresholds are unchanged).
//
// Each band has a today-anchored runway. Rows show one band-colored bar, a
// hollow intermediate-action marker when that date precedes expiration, a
// neutral expiration tick, and a fixed Expires column with the real date.
// Reduce exposure scales through its latest expiration; Monitor caps at 5yr.
//
// The page-level Timeline legend/popover and each band's context copy remain
// owned by the existing Leases page. This component changes only row/axis
// presentation; Incoming routing is display-only and never writes lease data.
//
// LEASES-TIMELINE-POLISH-S3: swapped the marker glyphs — Notice is now an
// open circle (○), Expiry a vertical end-cap line (│), matching the "you
// decide at Notice, the lease ends at Expiry" mental model. Added a
// date-label collision guard (expiry label always shows; nearby notice
// labels are staggered when they would overlap). Replaced
// the old binary "has notice passed" check with a three-state notice-window
// model (see resolveNoticeWindowState) so a passed notice with a live
// expiry reads "Notice open" with a countdown to the bounded window end
// (or lease expiry for a single-date notice) instead of "Notice lapsed".
//
// Drawer: position:fixed right panel (296px wide, 100vh) with CSS width transition.

import React, { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { T } from "../../app/tokens.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { resolveTimelineEndAnchor, isSentinelEndDate } from "../../features/leases/leaseTabConfig.js";
import { DrawerCloseButton } from "../components/DrawerCloseButton.jsx";
import { useDrawIn } from "../hooks/useDrawIn.js";
import { useRunwayEntrance } from "../hooks/useRunwayEntrance.js";
import { OfficeNameLabel } from "../../components/OfficeNameLabel.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { TIMELINE_BAR_PHASES } from "./timelineBarPhases.js";
import { sumReduceExposureAtRiskAnnualRent } from "../../features/leases/reduceExposure.js";
import { classifyLeaseHealth, deriveLeaseActBy } from "../../features/leases/deriveLeaseActions.js";
import { LEASE_STATUS_META } from "../../features/leases/leaseStatusLabels.js";
import { timelineLaneFor, incomingStartDate, compareIncomingStarts } from "./timelineLanes.js";
import { incomingRunway, INCOMING_HORIZON_DAYS, INCOMING_AXIS_TICKS } from "./incomingRunway.js";
import { IncomingRunway } from "./IncomingRunway.jsx";
import {
  getCurrentTermStartDate,
  isVerifiedLeaseDecisionDate,
  selectCurrentTermKeyDates,
  selectCurrentTermNoticeWindow,
} from "../../features/leases/leaseDetailDatesHelpers.js";

// ─── Group configuration ──────────────────────────────────────────────────────
//
// LEASES-TIMELINE-NOTICE-PHASES-S1: each lane now also carries its own axis
// scale (Today → the lane's far edge) so every section renders ticks sized
// to its own time horizon instead of one shared scale across all four bands.
//   axisFarEdgeDays        — right edge of this band's axis, in days from today
//   axisNearThresholdDays  — where this band's decision window opens
//   axisCapped             — true only for Monitor; markers beyond the far
//                            edge render clamped to the edge, labeled "…capped"
//   axisTicks              — { days, label } header labels and body gridlines

export const TIMELINE_GROUPS = [
  {
    bucket: "incoming",
    label: "Committed / incoming",
    color: T.mid,
    tintBg: T.hair,
    border: T.line,
    isIncoming: true,
    expandedByDefault: false,
    axisFarEdgeDays: INCOMING_HORIZON_DAYS,
    axisNearThresholdDays: 0,
    axisCapped: true,
    axisTicks: INCOMING_AXIS_TICKS,
  },
  {
    bucket:          "decide-now",
    label:           "Next 90 days",
    color:           T.leaseActionBuckets["decide-now"].foreground,
    tintBg:          T.leaseActionBuckets["decide-now"].tint,
    border:          T.leaseActionBuckets["decide-now"].tint,
    expandedByDefault: true,
    axisFarEdgeDays:       90,
    axisNearThresholdDays: 0,
    axisCapped:            false,
    axisTicks: [
      { days: 30, label: "1mo" },
      { days: 60, label: "2mo" },
      { days: 90, label: "3mo" },
    ],
  },
  {
    bucket:          "negotiate",
    label:           "Within a year",
    color:           T.leaseActionBuckets.negotiate.foreground,
    tintBg:          T.leaseActionBuckets.negotiate.tint,
    border:          T.leaseActionBuckets.negotiate.tint,
    expandedByDefault: true,
    axisFarEdgeDays:       365,
    axisNearThresholdDays: 90,
    axisCapped:            false,
    axisTicks: [
      { days: 90,  label: "3mo" },
      { days: 180, label: "6mo" },
      { days: 270, label: "9mo" },
      { days: 365, label: "12mo" },
    ],
  },
  {
    bucket:          "reduce-exposure",
    label:           "Reduce exposure",
    color:           T.leaseActionBuckets["reduce-exposure"].foreground,
    tintBg:          T.leaseActionBuckets["reduce-exposure"].tint,
    border:          T.leaseActionBuckets["reduce-exposure"].tint,
    isVacated:       true,
    expandedByDefault: true,
    axisFarEdgeDays:       1095,
    axisNearThresholdDays: 0,
    axisCapped:            false,
    axisTicks: [
      { days: 365,  label: "1yr" },
      { days: 730,  label: "2yr" },
      { days: 1095, label: "3yr" },
    ],
  },
  {
    bucket:          "monitor",
    label:           "Beyond a year",
    color:           T.leaseActionBuckets.monitor.foreground,
    tintBg:          T.leaseActionBuckets.monitor.tint,
    border:          T.leaseActionBuckets.monitor.tint,
    expandedByDefault: false,
    axisFarEdgeDays:       1825,
    axisNearThresholdDays: 730,
    axisCapped:            true,
    axisTicks: [
      { days: 365,  label: "1yr" },
      { days: 730,  label: "2yr" },
      { days: 1095, label: "3yr" },
      { days: 1460, label: "4yr" },
      { days: 1825, label: "5yr…capped" },
    ],
  },
  {
    bucket:            "rolling-mtm",
    label:             "Rolling / MTM",
    color:             T.mid,
    tintBg:            T.hair,
    border:            T.line,
    expandedByDefault: true,
    axisFarEdgeDays:   365,
    axisNearThresholdDays: 0,
    axisCapped:        false,
    axisTicks:         [],
  },
  // LEASES-TIMELINE-CLEANUP: leases on closed/non-active offices with a past
  // anchor. No forward axis; group header only.
  {
    bucket:          "expired",
    label:           "Expired",
    color:           T.mid,
    tintBg:          T.hair,
    border:          T.line,
    expandedByDefault: false,
    isFooter:         true,
    axisFarEdgeDays:       0,
    axisNearThresholdDays: 0,
    axisCapped:            false,
    axisTicks:             [],
  },
];

// One-line "why is this lease in this bucket" descriptor, rendered next to
// each band's pill in the group header (LEASES-TIMELINE-V2-S1). Mirrors the
// same 90/365/730-day boundaries as TIMELINE_GROUPS / deriveLeaseActions.js —
// presentation copy only, no new thresholds.
export const BAND_CONTEXT = {
  "incoming":     "signed, not yet occupied",
  "decide-now":    "lease expiry within 90 days - act this quarter",
  "negotiate":     "3-12 months out - plan ahead",
  "reduce-exposure": "vacated lease carrying rent - sublease or wind down at term",
  "rolling-mtm":    "active lease past its stated expiry; earliest termination is shown when known",
  "monitor":       "12+ months out - watch and revisit later",
  // LEASES-TIMELINE-CLEANUP
  "expired":       "lease expiry has passed and no rolling term is recorded",
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const TIMELINE_ROW_COLUMNS = "minmax(180px, 210px) 170px minmax(260px, 1fr) 132px";
const INCOMING_ROW_COLUMNS = "minmax(180px, 210px) 170px minmax(260px, 1fr) 240px";
// Match the List's tier styling, not the timing band's palette.
const OUTLOOK_TIER_STYLE = {
  green: { color: LEASE_STATUS_META.in_use.color, background: LEASE_STATUS_META.in_use.bg },
  amber: { color: T.ragAmber, background: T.ragAmberBg },
  slate: { color: LEASE_STATUS_META.vacant_held.color, background: LEASE_STATUS_META.vacant_held.bg },
  grey: { color: LEASE_STATUS_META.expired.color, background: LEASE_STATUS_META.expired.bg },
  blue: { color: LEASE_STATUS_META.draft.color, background: LEASE_STATUS_META.draft.bg },
  red: { color: T.ragRed, background: T.ragRedBg },
};

function OutlookChip({ lease, criticalDates }) {
  const outlook = classifyLeaseHealth(lease, criticalDates);
  return (
    <span data-testid={`timeline-outlook-${lease.id}`} style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      borderRadius: 6, height: 22, lineHeight: 1, padding: "0 10px",
      fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap",
      minWidth: 90, textAlign: "center",
      ...(OUTLOOK_TIER_STYLE[outlook.colorTier] ?? OUTLOOK_TIER_STYLE.grey),
    }}>
      {outlook.word}
    </span>
  );
}

function parseIso(str) {
  if (!str) return null;
  const d = new Date(str.includes("T") ? str : str + "T00:00:00Z");
  return isNaN(d.getTime()) ? null : d;
}

function parseCalendarDateUtc(value, fallbackDate) {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  }
  if (value != null) {
    const parsed = parseIso(String(value).slice(0, 10));
    if (parsed) return parsed;
  }
  if (fallbackDate instanceof Date && Number.isFinite(fallbackDate.getTime())) {
    return new Date(Date.UTC(
      fallbackDate.getFullYear(),
      fallbackDate.getMonth(),
      fallbackDate.getDate(),
    ));
  }
  return null;
}

function fmtDateShort(dateOrStr) {
  const d = dateOrStr instanceof Date ? dateOrStr : parseIso(dateOrStr);
  if (!d) return "—";
  return `${_MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function fmtDue(days) {
  if (days === null || days === undefined) return "—";
  if (days < 0) return "—";
  if (days === 0) return "Today";
  if (days < 90)  return `${days}d`;
  const months = Math.round(days / 30.4);
  return `${months}mo`;
}

const NOTICE_ACTION_TYPES = new Set(["renewal_notice", "notice_deadline", "break_notice"]);
const COVERED_ACTION_DATE_TYPES = new Set([
  "renewal_notice",
  "renewal_option",
  "break_option",
  "break_notice",
  "notice_deadline",
]);
const LENS_DECISION_DATE_TYPES = new Set([
  "renewal_notice",
  "renewal_option",
  "break_option",
  "break_notice",
  "notice_deadline",
]);
const LENS_STANDARD_BUCKETS = new Set(["decide-now", "negotiate", "monitor"]);

function isSourceBackedActionDate(type, dateValue, lease, criticalDates, today = todayUtcMidnight()) {
  const date = parseCalendarDateUtc(dateValue);
  if (!date || !type) return false;
  const targetKey = date.toISOString().slice(0, 10);
  const termStart = getCurrentTermStartDate(criticalDates, lease);
  const startKey = termStart instanceof Date
    ? `${termStart.getFullYear()}-${String(termStart.getMonth() + 1).padStart(2, "0")}-${String(termStart.getDate()).padStart(2, "0")}`
    : parseCalendarDateUtc(termStart)?.toISOString().slice(0, 10) || null;
  const expiryDt = resolveExpiryDate(lease);
  const expiryKey = expiryDt?.toISOString().slice(0, 10) || null;
  if ((startKey && targetKey < startKey) || (expiryKey && targetKey >= expiryKey)) return false;

  const expectedTypes = type === "break_notice"
    ? new Set(["break_notice", "break_option"])
    : new Set([type]);
  const fromCriticalDate = selectCurrentTermKeyDates(criticalDates, lease, today).eligibleDates
    .some((criticalDate) => {
      const criticalDateKey = parseCalendarDateUtc(criticalDate.dueDate)?.toISOString().slice(0, 10);
      if (!expectedTypes.has(criticalDate.type) || criticalDateKey !== targetKey) return false;
      if (!isVerifiedLeaseDecisionDate(criticalDate)) return false;
      if (type !== "break_notice" || criticalDate.type === "break_notice") return true;
      const category = String(
        criticalDate.categoryRaw ?? criticalDate.category_raw ?? criticalDate.label ?? "",
      );
      return /\bnotice\b/i.test(category);
    });

  const fromLeaseField = type === "notice_deadline"
    ? isVerifiedLeaseDecisionDate(lease, "nextNoticeDeadline")
      && parseCalendarDateUtc(lease?.nextNoticeDeadline)?.toISOString().slice(0, 10) === targetKey
    : type === "break_option"
      ? isVerifiedLeaseDecisionDate(lease, "breakDate")
        && parseCalendarDateUtc(lease?.breakDate)?.toISOString().slice(0, 10) === targetKey
      : false;
  if (!fromCriticalDate && !fromLeaseField) return false;

  if (!NOTICE_ACTION_TYPES.has(type)) return true;
  const todayDt = parseCalendarDateUtc(today) || todayUtcMidnight();
  const daysUntil = Math.floor((date.getTime() - todayDt.getTime()) / 86_400_000);
  if (daysUntil >= 0 && daysUntil <= 90) return true;

  const windowTypes = type === "break_notice"
    ? new Set(["break_notice", "break_option"])
    : new Set([type]);
  const window = selectCurrentTermNoticeWindow(criticalDates, lease, today, windowTypes);
  if (window.state !== "notice-open") return false;
  const openWindowDate = window.periodEndDateValue || window.noticeDateValue;
  return parseCalendarDateUtc(openWindowDate)?.toISOString().slice(0, 10) === targetKey;
}

function isVerifiedTimelineDecisionAction(lease, type, dateValue, criticalDates, today) {
  const expectedType = lease?._timelineDecisionMarkerType
    ?? (lease?._timelineDecisionLensDriven ? lease.actByType : null);
  const expectedDate = lease?._timelineDecisionMarkerDate
    ?? (lease?._timelineDecisionLensDriven ? lease.actByDate : null);
  if (expectedType !== type || !expectedDate) return false;
  const selected = verifiedDecisionAction(lease, criticalDates, today);
  const targetKey = parseCalendarDateUtc(dateValue)?.toISOString().slice(0, 10);
  return !!selected
    && selected.type === type
    && parseCalendarDateUtc(expectedDate)?.toISOString().slice(0, 10) === targetKey
    && selected.date.toISOString().slice(0, 10) === targetKey;
}

function isTimelineActionDateBacked(lease, type, dateValue, criticalDates, today) {
  return isVerifiedTimelineDecisionAction(lease, type, dateValue, criticalDates, today)
    || isSourceBackedActionDate(type, dateValue, lease, criticalDates, today);
}

function isRealNoticeDate(type, dateValue, lease, criticalDates, today) {
  if (NOTICE_ACTION_TYPES.has(type)) {
    return isSourceBackedActionDate(type, dateValue, lease, criticalDates, today);
  }
  if (type !== "break_option") return false;
  const targetKey = parseCalendarDateUtc(dateValue)?.toISOString().slice(0, 10);
  if (!targetKey || !isSourceBackedActionDate(type, dateValue, lease, criticalDates, today)) {
    return false;
  }
  return selectCurrentTermKeyDates(criticalDates, lease, today).eligibleDates.some((criticalDate) => {
    const category = String(
      criticalDate.categoryRaw ?? criticalDate.category_raw ?? criticalDate.label ?? "",
    );
    return criticalDate.type === "break_option"
      && parseCalendarDateUtc(criticalDate.dueDate)?.toISOString().slice(0, 10) === targetKey
      && /\bnotice\b/i.test(category);
  });
}

export function getTimelineDateCoverage(leases = [], cdByLease = {}, today = todayUtcMidnight()) {
  let expiryDateCount = 0;
  let currentTermActionDateCount = 0;
  let earliestTerminationDateCount = 0;

  for (const lease of leases) {
    const expiryDt = resolveExpiryDate(lease);
    if (expiryDt) expiryDateCount += 1;
    if (parseCalendarDateUtc(lease.earliestTermination ?? lease.earliest_termination)) {
      earliestTerminationDateCount += 1;
    }

    const selection = selectCurrentTermKeyDates(cdByLease[lease.id] || [], lease, today);
    const hasCriticalDate = selection.eligibleDates.some((date) =>
      COVERED_ACTION_DATE_TYPES.has(date.type) && parseCalendarDateUtc(date.dueDate),
    );
    const expiryKey = expiryDt ? expiryDt.toISOString().slice(0, 10) : null;
    const startKey = parseCalendarDateUtc(lease.startDate)?.toISOString().slice(0, 10) || null;
    const isInCurrentTerm = (value) => {
      const date = parseCalendarDateUtc(value);
      if (!date) return false;
      const key = date.toISOString().slice(0, 10);
      return (!startKey || key >= startKey) && (!expiryKey || key <= expiryKey);
    };
    const hasLeaseFieldDate = isInCurrentTerm(lease.nextNoticeDeadline)
      || isInCurrentTerm(lease.breakDate);

    if (hasCriticalDate || hasLeaseFieldDate) currentTermActionDateCount += 1;
  }

  return {
    leaseCount: leases.length,
    expiryDateCount,
    currentTermActionDateCount,
    earliestTerminationDateCount,
  };
}

function fmtCost(amount) {
  if (!amount || !Number.isFinite(Number(amount))) return "—";
  const n = Number(amount);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M/yr`;
  if (Math.abs(n) >= 1_000)     return `$${Math.round(n / 1_000)}K/yr`;
  return `$${Math.round(n).toLocaleString()}/yr`;
}

function fmtCostTotal(amount) {
  if (!amount || !Number.isFinite(Number(amount))) return "—";
  const n = Number(amount);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M/yr`;
  if (Math.abs(n) >= 1_000)     return `$${Math.round(n / 1_000)}K/yr`;
  return `$${Math.round(n).toLocaleString()}/yr`;
}

// Legacy notice-date resolver, still used by the drawer's "Notice date" Key
// Figure (falls back to soonestPossibleExpiration when no notice-type
// critical date exists — appropriate there since it's just informational).
function noticeDate(lease, cdsForLease) {
  const selection = selectCurrentTermKeyDates(cdsForLease, lease);
  const cd = selection.nextUpcomingWindowDate || selection.mostRecentElapsedWindowDate;
  if (cd?.dueDate) return parseIso(cd.dueDate);
  if (lease.soonestPossibleExpiration) return parseIso(lease.soonestPossibleExpiration);
  return null;
}

// ─── Notice + Expiry date resolution (row model) ───────────────────────────────
//
// LEASES-TIMELINE-NOTICE-PHASES-S1: the row track needs the *real* notice
// date only (no soonestPossibleExpiration fallback — that would blur the
// distinction between "when I must act" and "when the lease ends"). When no
// real notice date exists, phase computation anchors off Expiry instead via
// resolveTimelineEndAnchor, per the existing fallback hierarchy.

function resolveNoticeDate(lease, cdsForLease, today) {
  return resolveNoticeWindowState(lease, cdsForLease, today).noticeDt;
}

function resolveExpiryDate(lease) {
  if (!isSentinelEndDate(lease.endDate)) {
    return parseIso(lease.endDate);
  }
  if (lease.soonestPossibleExpiration) {
    return parseIso(lease.soonestPossibleExpiration);
  }
  return null;
}

function todayUtcMidnight() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function daysBetween(fromDt, toDt) {
  return Math.round((toDt - fromDt) / (1000 * 60 * 60 * 24));
}

function verifiedDecisionAction(lease, cdsForLease, today) {
  const sourceDates = cdsForLease || [];
  const hasVerifiedCriticalDate = sourceDates.some((date) =>
    LENS_DECISION_DATE_TYPES.has(date.type)
    && isVerifiedLeaseDecisionDate(date),
  );
  const hasVerifiedLeaseField = isVerifiedLeaseDecisionDate(lease, "nextNoticeDeadline")
    || isVerifiedLeaseDecisionDate(lease, "breakDate");
  if (!hasVerifiedCriticalDate && !hasVerifiedLeaseField) return null;

  const criticalDates = sourceDates.filter((date) =>
    date.type !== "rolling_review"
    && (!LENS_DECISION_DATE_TYPES.has(date.type) || isVerifiedLeaseDecisionDate(date)),
  );
  const selection = selectCurrentTermKeyDates(criticalDates, lease, today);
  const todayKey = parseCalendarDateUtc(today)?.toISOString().slice(0, 10);
  const termStartKey = parseCalendarDateUtc(getCurrentTermStartDate(criticalDates, lease))
    ?.toISOString().slice(0, 10);
  const expiryKey = resolveExpiryDate(lease)?.toISOString().slice(0, 10);
  const candidates = [];
  const addCandidate = (value, type, state = "notice-future", allowPast = false) => {
    const date = parseCalendarDateUtc(value);
    if (!date || !type) return;
    const key = date.toISOString().slice(0, 10);
    if ((termStartKey && key < termStartKey) || (expiryKey && key >= expiryKey)) return;
    if (!allowPast && todayKey && key < todayKey) return;
    candidates.push({ date, type, state });
  };

  // Keep the established resolver for an already-open notice, while extending
  // eligibility to verified current-term dates across the full lease horizon.
  const action = deriveLeaseActBy(lease, criticalDates, today);
  if (
    action.actByDate
    && action.actByType
    && action.actByType !== "expiry"
    && (action.daysToActBy >= 0 || action.actByState === "notice-open")
  ) {
    addCandidate(
      action.actByDate,
      action.actByType,
      action.actByState || "notice-future",
      action.actByState === "notice-open",
    );
  }

  // deriveLeaseActBy intentionally limits ordinary notice actions to 90 days.
  // The decision lens also considers verified dates in the 3–12 month window.
  for (const date of selection.eligibleDates) {
    if (!LENS_DECISION_DATE_TYPES.has(date.type) || !isVerifiedLeaseDecisionDate(date)) continue;
    addCandidate(date.dueDate, date.type);
  }

  for (const [fieldName, type] of [
    ["nextNoticeDeadline", "notice_deadline"],
    ["breakDate", "break_option"],
  ]) {
    if (isVerifiedLeaseDecisionDate(lease, fieldName)) {
      addCandidate(lease[fieldName], type);
    }
  }

  candidates.sort((a, b) => a.date - b.date);
  return candidates[0] || null;
}

export function hasVerifiedDecisionDate(leases = [], cdByLease = {}, today = todayUtcMidnight()) {
  return leases.some((lease) => verifiedDecisionAction(lease, cdByLease[lease.id], today));
}

export function applyTimelineLens(leases = [], cdByLease = {}, viewBy = "expiry", today = todayUtcMidnight()) {
  return leases.map((lease) => {
    const originalBucket = lease.action_window_bucket ?? "monitor";
    if (
      !LENS_STANDARD_BUCKETS.has(originalBucket)
      || lease.status === "expired"
      || lease.posture === "expired"
      || lease.posture === "holdover"
    ) return lease;

    const expiryDate = resolveExpiryDate(lease);
    const decision = verifiedDecisionAction(lease, cdByLease[lease.id], today);
    const decisionDrives = viewBy === "decision"
      && !!decision
      && (!expiryDate || decision.date < expiryDate);
    const drivingDate = decisionDrives ? decision.date : expiryDate;
    const drivingType = decisionDrives ? decision.type : "expiry";
    const days = drivingDate ? daysBetween(today, drivingDate) : null;
    const bucket = days === null || days > 365
      ? "monitor"
      : days <= 90
        ? "decide-now"
        : "negotiate";
    const expiryDays = expiryDate ? daysBetween(today, expiryDate) : null;
    const expiryBucket = expiryDays === null || expiryDays > 365
      ? "monitor"
      : expiryDays <= 90
        ? "decide-now"
        : "negotiate";
    const movedFrom = viewBy === "decision" && bucket !== expiryBucket
      ? TIMELINE_GROUPS.find((group) => group.bucket === expiryBucket)?.label ?? expiryBucket
      : null;
    const dateKey = drivingDate?.toISOString().slice(0, 10) ?? null;

    return {
      ...lease,
      actByDate: dateKey,
      actByType: drivingType,
      days_to_next_action: days,
      daysToActBy: days,
      next_action_date: drivingType === "expiry" ? null : dateKey,
      next_action_type: drivingType === "expiry" ? null : drivingType,
      actByState: decisionDrives ? decision.state : null,
      action_window_bucket: bucket,
      action_short_reason: drivingType === "expiry" ? "Lease expiry" : actionTypeLabel(drivingType),
      _timelineDecisionMarkerDate: decision?.date.toISOString().slice(0, 10) ?? null,
      _timelineDecisionMarkerType: decision?.type ?? null,
      _timelineDecisionLensDriven: decisionDrives,
      _timelineMovedFrom: movedFrom,
    };
  });
}

function edgeLabelTransform(alignment, verticalOffset = 0) {
  const horizontal = alignment === "start"
    ? "translateX(0%)"
    : alignment === "end"
      ? "translateX(-100%)"
      : "translateX(-50%)";
  return verticalOffset ? `${horizontal} translateY(${verticalOffset}px)` : horizontal;
}

function measuredLabelAlignment(label, track, pct) {
  if (pct <= 0) return "start";
  if (pct >= 100) return "end";
  if (!label || !track) return "center";

  const trackRect = track.getBoundingClientRect();
  const labelRect = label.getBoundingClientRect();
  const anchorX = trackRect.left + (pct / 100) * trackRect.width;
  if (anchorX - labelRect.width / 2 < trackRect.left) return "start";
  if (anchorX + labelRect.width / 2 > trackRect.right) return "end";
  return "center";
}

function labelHorizontalBounds(label, track, pct, alignment) {
  if (!label || !track) return null;
  const trackRect = track.getBoundingClientRect();
  const labelRect = label.getBoundingClientRect();
  const anchorX = trackRect.left + (pct / 100) * trackRect.width;
  const left = alignment === "start"
    ? anchorX
    : alignment === "end"
      ? anchorX - labelRect.width
      : anchorX - labelRect.width / 2;
  return { left, right: left + labelRect.width };
}

function horizontalLabelGap(a, b) {
  if (!a || !b) return Infinity;
  if (a.right < b.left) return b.left - a.right;
  if (b.right < a.left) return a.left - b.right;
  return -Math.min(a.right, b.right) + Math.max(a.left, b.left);
}

// Countdown column text: "Nd to notice" before the notice date passes,
// flipping to "Expires in Nd" once it has passed (or when there is no real
// notice date at all, in which case the anchor was already Expiry).
function countdownInfo(lease, cdsForLease, today) {
  const noticeDt = resolveNoticeDate(lease, cdsForLease, today);
  const expiryDt = resolveExpiryDate(lease);
  const daysToNotice = noticeDt ? daysBetween(today, noticeDt) : null;
  const daysToExpiry = expiryDt ? daysBetween(today, expiryDt) : null;

  if (daysToNotice !== null && daysToNotice > 0) {
    return { text: `${fmtDue(daysToNotice)} to notice`, passed: false };
  }
  if (daysToExpiry !== null && daysToExpiry > 0) {
    return { text: `Expires in ${fmtDue(daysToExpiry)}`, passed: true };
  }
  return { text: "Expired", passed: true };
}

// LEASES-TIMELINE-POLISH-S3: notice-window state machine.
//
// A lease's notice/expiry pair is always in exactly one of four states,
// evaluated against `today`:
//   "no-notice"     — no valid current-term notice/break window exists
//   "notice-future" — the notice date hasn't arrived yet
//   "notice-open"   — the notice date has passed but expiry hasn't; the
//                      decision window is open and the countdown that
//                      matters is now to Expiry, not to a lapsed notice
//   "expired"       — both notice and expiry have passed
//
// This replaces the old boolean "has the notice passed" check, which
// produced misleading "Notice lapsed" copy for leases that were still very
// much live (notice passed, expiry months away). Exported so the
// action-derivation logic can be unit tested directly.
export function resolveNoticeWindowState(lease, cdsForLease, today) {
  const expiryDt = resolveExpiryDate(lease);
  if (Object.prototype.hasOwnProperty.call(lease, "actByDate")) {
    const noticeDt = lease.actByDate
      ? parseCalendarDateUtc(lease.actByDate)
      : null;
    const backed = noticeDt && lease.actByType !== "expiry"
      && isTimelineActionDateBacked(lease, lease.actByType, lease.actByDate, cdsForLease, today);
    if (!backed) {
      return {
        state: expiryDt && expiryDt <= today ? "expired" : "no-notice",
        noticeDt: null,
        expiryDt,
        noticeType: null,
      };
    }
    return {
      state: NOTICE_ACTION_TYPES.has(lease.actByType)
        ? (isRealNoticeDate(lease.actByType, lease.actByDate, lease, cdsForLease, today)
          || isVerifiedTimelineDecisionAction(lease, lease.actByType, lease.actByDate, cdsForLease, today)
          ? lease.actByState || (
            noticeDt < today
              ? expiryDt && expiryDt > today ? "notice-open" : "expired"
              : "notice-future"
          )
          : "no-notice")
        : "no-notice",
      noticeDt,
      expiryDt,
      noticeType: lease.actByType,
      periodEndDt: null,
      periodEndDateValue: null,
    };
  }
  if (!expiryDt) {
    return { state: "no-notice", noticeDt: null, expiryDt, noticeType: null };
  }

  const verifiedCriticalDates = (cdsForLease || []).filter((date) =>
    !COVERED_ACTION_DATE_TYPES.has(date.type)
    || isVerifiedLeaseDecisionDate(date),
  );
  const selectedWindow = selectCurrentTermNoticeWindow(verifiedCriticalDates, lease, today);
  const noticeDt = parseCalendarDateUtc(selectedWindow.noticeDateValue, selectedWindow.noticeDt);
  const periodEndDt = parseCalendarDateUtc(
    selectedWindow.periodEndDateValue,
    selectedWindow.periodEndDt,
  );
  const selectedDateValue = selectedWindow.periodEndDateValue || selectedWindow.noticeDateValue;
  const isEligibleNotice = selectedWindow.state !== "no-notice"
    && isRealNoticeDate(
      selectedWindow.noticeType,
      selectedDateValue,
      lease,
      cdsForLease,
      today,
    );
  const state = isEligibleNotice || selectedWindow.state === "expired"
    ? selectedWindow.state
    : "no-notice";
  return {
    state,
    noticeDt: state === "no-notice" ? null : noticeDt,
    expiryDt,
    noticeType: state === "no-notice" ? null : selectedWindow.noticeType,
    periodEndDt: state === "no-notice" ? null : periodEndDt,
    periodEndDateValue: state === "no-notice" ? null : selectedWindow.periodEndDateValue,
  };
}

const ACTION_TYPE_LABELS = {
  renewal_notice: "Renewal notice",
  break_notice: "Break notice",
  break_option: "Break option",
  notice_deadline: "Notice deadline",
  renewal_option: "Renewal option",
  rolling_review: "Rolling review",
};

function actionTypeLabel(type) {
  if (!type || type === "expiration" || type === "expiry") return null;
  return ACTION_TYPE_LABELS[type]
    || String(type).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resolveActionPoint(lease, cdsForLease, today, expiryDt = resolveExpiryDate(lease)) {
  if (Object.prototype.hasOwnProperty.call(lease, "actByDate")) {
    if (lease.actByType === "expiry") {
      const markerDate = lease._timelineDecisionMarkerDate;
      const markerType = lease._timelineDecisionMarkerType;
      const date = markerDate ? parseCalendarDateUtc(markerDate) : null;
      return date
        && isVerifiedTimelineDecisionAction(lease, markerType, markerDate, cdsForLease, today)
        ? { date, label: actionTypeLabel(markerType) || "Notice" }
        : null;
    }
    if (!lease.actByDate) return null;
    const actByDt = parseCalendarDateUtc(lease.actByDate);
    const days = lease.days_to_next_action ?? lease.daysToActBy;
    return actByDt && (days > 0 || lease._timelineDecisionLensDriven)
      && isTimelineActionDateBacked(lease, lease.actByType, lease.actByDate, cdsForLease, today)
      ? { date: actByDt, label: actionTypeLabel(lease.actByType) || "Notice" }
      : null;
  }
  if (!expiryDt) return null;
  const explicitDate = parseCalendarDateUtc(lease.next_action_date);
  const explicitLabel = actionTypeLabel(lease.next_action_type);
  if (
    explicitDate && explicitLabel && explicitDate > today && explicitDate < expiryDt
    && isSourceBackedActionDate(
      lease.next_action_type,
      lease.next_action_date,
      lease,
      cdsForLease,
      today,
    )
  ) {
    return { date: explicitDate, label: explicitLabel };
  }

  const windowState = resolveNoticeWindowState(lease, cdsForLease, today);
  const label = actionTypeLabel(windowState.noticeType);
  if (
    windowState.state === "notice-future"
    && windowState.noticeDt
    && windowState.noticeDt > today
    && (!expiryDt || windowState.noticeDt < expiryDt)
    && isRealNoticeDate(
      windowState.noticeType,
      windowState.periodEndDateValue || windowState.noticeDt,
      lease,
      cdsForLease,
      today,
    )
  ) {
    return { date: windowState.noticeDt, label: label || "Notice" };
  }
  return null;
}

function expiresInfo(lease, today) {
  const expiryDt = resolveExpiryDate(lease);
  if (!expiryDt) return "—";
  const days = daysBetween(today, expiryDt);
  if (days <= 0) return "Expired";
  return fmtDue(days);
}

// ─── Per-band axis header ───────────────────────────────────────────────────────
//
// Labels span Today (left edge, 0%) → the band's far edge (100%).
// Guide strokes belong only to body lanes, not the column-header area.

function AxisHeader({ group }) {
  const far = group.axisFarEdgeDays;

  return (
    <div
      data-testid={`timeline-axis-${group.bucket}`}
      data-axis-days={far}
      aria-label={`${group.label} runway axis`}
      style={{ position: "relative", height: 34, minWidth: 0, width: "100%" }}
    >
      <span style={{
        position: "absolute",
        top: 0,
        left: 0,
        fontSize: T.fsXxs,
        fontWeight: T.fwBold,
        textTransform: "uppercase",
        letterSpacing: ".07em",
        color: T.light,
      }}>
        Runway
      </span>
      {group.axisTicks.map((tick) => {
        const pct = Math.min(100, (tick.days / far) * 100);
        return (
            <span
              key={tick.days}
              data-testid={`timeline-axis-tick-${group.bucket}-${tick.days}`}
              data-axis-percent={pct}
              style={{
              position:   "absolute",
              top:        17,
              left:       `${pct}%`,
              transform:  pct >= 100 ? "translateX(-100%)" : "translateX(-50%)",
              fontSize:   T.fsXs,
              color:      T.light,
              whiteSpace: "nowrap",
            }}>
              {tick.label}
            </span>
        );
      })}

      {/* Keep the label's original offset, including the removed 1px border. */}
      <span style={{
        position:   "absolute",
        top:        13,
        bottom:     0,
        left:       0,
      }}>
        <span style={{
          position:   "absolute",
          top:        4,
          left:       4,
          fontSize:   T.fsXs,
          fontWeight: T.fwBold,
          color:      T.light,
          whiteSpace: "nowrap",
        }}>
          Today
        </span>
      </span>
    </div>
  );
}

function ActionRunway({ lease, cdsForLease = [], group, index = 0 }) {
  const today = useMemo(() => todayUtcMidnight(), []);
  const expiryDt = resolveExpiryDate(lease);
  const hasKnownExpiry = !!expiryDt;
  const far = Math.max(1, group.axisFarEdgeDays);
  const daysToExpiry = hasKnownExpiry ? daysBetween(today, expiryDt) : null;
  const rawExpiryPct = hasKnownExpiry ? (daysToExpiry / far) * 100 : null;
  const expiryPct = rawExpiryPct === null ? null : Math.max(0, Math.min(100, rawExpiryPct));
  const barWidthPct = expiryPct === null ? 100 : expiryPct;
  const expiryBeyondAxis = hasKnownExpiry && !!group.axisCapped && rawExpiryPct > 100;
  const actionPoint = resolveActionPoint(lease, cdsForLease, today, expiryDt);
  const actionDays = actionPoint ? daysBetween(today, actionPoint.date) : null;
  const actionPct = actionDays === null
    ? null
    : Math.max(0, Math.min(100, (actionDays / far) * 100));
  const commenceDt = lease.startDate ? parseCalendarDateUtc(lease.startDate) : null;
  const commenceDays = commenceDt && commenceDt > today ? daysBetween(today, commenceDt) : null;
  const commenceRawPct = commenceDays === null ? null : (commenceDays / far) * 100;
  const commencePct = commenceRawPct === null ? null : Math.max(0, Math.min(100, commenceRawPct));
  const entrance = useRunwayEntrance(index);
  const isRollingLane = group.bucket === "rolling-mtm";

  return (
    <div
      data-testid={`timeline-track-${lease.id}`}
      data-runway-bucket={group.bucket}
      data-axis-days={far}
      data-expiry-percent={expiryPct ?? ""}
      style={{ position: "relative", minWidth: 0, height: 34 }}
    >
      <div
        data-testid={`runway-lane-${lease.id}`}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 11,
          height: 7,
          borderRadius: 4,
          background: T.hair,
        }}
      />
      {group.axisTicks.map((tick) => {
        const pct = Math.min(100, (tick.days / far) * 100);
        return (
          <span
            key={tick.days}
            data-testid={`runway-gridline-${lease.id}-${tick.days}`}
            style={{
              position: "absolute",
              left: `${pct}%`,
              top: 5,
              bottom: 4,
              width: 1,
              background: T.mid,
              opacity: 0.18,
              zIndex: 1,
            }}
          />
        );
      })}
      <div
        data-testid={`runway-bar-${lease.id}`}
        style={{
          position: "absolute",
          left: 0,
          top: 11,
          width: `${(isRollingLane ? 100 : barWidthPct) * entrance.fill}%`,
          height: 7,
          borderRadius: 4,
          background: isRollingLane
            ? `repeating-linear-gradient(135deg, ${T.hair}, ${T.hair} 5px, ${T.line} 5px, ${T.line} 10px)`
            : hasKnownExpiry ? group.color : group.tint,
          borderRight: hasKnownExpiry ? undefined : `1px dashed ${group.color}`,
          borderLeft: isRollingLane ? `1px dashed ${group.color}` : undefined,
          zIndex: 2,
        }}
      />
      {actionPct !== null && (
        <span
          data-testid={`action-marker-${lease.id}`}
          data-decision-lens-driven={lease._timelineDecisionLensDriven ? "true" : undefined}
          title={`${actionPoint.label} · ${fmtDateShort(actionPoint.date)}`}
          style={{
            position: "absolute",
            left: `${actionPct}%`,
            top: -3,
            transform: actionPct > 84 ? "translateX(-100%)" : "translateX(-50%)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            maxWidth: "100%",
            padding: "1px 5px",
            borderRadius: 8,
            border: `1px solid ${lease._timelineDecisionLensDriven ? "var(--v2-accent)" : group.color}`,
            background: lease._timelineDecisionLensDriven ? "var(--v2-blue-tint)" : "var(--v2-card)",
            color: lease._timelineDecisionLensDriven ? "var(--v2-accent)" : group.color,
            fontSize: T.fsXxs,
            fontWeight: T.fwSemibold,
            lineHeight: 1.15,
            whiteSpace: "nowrap",
            zIndex: 3,
          }}
        >
          <span aria-hidden="true" style={{
            width: 6,
            height: 6,
            flex: "0 0 6px",
            borderRadius: "50%",
            border: `1.5px solid ${lease._timelineDecisionLensDriven ? "var(--v2-accent)" : group.color}`,
            background: lease._timelineDecisionLensDriven ? "var(--v2-accent)" : "var(--v2-card)",
          }} />
          {actionPoint.label}
        </span>
      )}
      {commencePct !== null && (
        <>
          <span
            data-testid={`commencement-marker-${lease.id}`}
            title={`Commences ${fmtDateShort(commenceDt)}`}
            style={{
              position: "absolute",
              left: `${commencePct}%`,
              top: 8,
              width: 8,
              height: 8,
              transform: "translateX(-50%) rotate(45deg)",
              border: `1.5px solid ${T.mid}`,
              background: "var(--v2-card)",
              zIndex: 4,
            }}
          />
          <span
            data-testid={`commencement-label-${lease.id}`}
            style={{
              position: "absolute",
              left: `${commencePct}%`,
              top: 24,
              transform: commencePct >= 100 ? "translateX(-100%)" : "translateX(-50%)",
              color: T.mid,
              fontSize: T.fsXxs,
              whiteSpace: "nowrap",
            }}
          >
            {commenceRawPct > 100 ? "…capped" : `Commences ${fmtDateShort(commenceDt)}`}
          </span>
        </>
      )}
      {hasKnownExpiry && (
        <span
          data-testid={`expiry-marker-${lease.id}`}
          title={`Expires ${fmtDateShort(expiryDt)}`}
          style={{
            position: "absolute",
            left: `${expiryPct}%`,
            top: 7,
            transform: "translateX(-50%)",
            width: 2,
            height: 15,
            borderRadius: 1,
            background: T.dark,
            boxShadow: `0 0 0 1px var(--v2-card)`,
            opacity: entrance.cap,
            zIndex: 4,
          }}
        />
      )}
      {!hasKnownExpiry && (
        <span
          data-testid={`runway-open-ended-${lease.id}`}
          aria-label="No known expiration"
          title="No known expiration · open-ended runway"
          style={{
            position: "absolute",
            left: "100%",
            top: 6,
            transform: "translateX(-100%)",
            color: group.color,
            fontSize: T.fsMd,
            fontWeight: T.fwBold,
            lineHeight: 1,
            zIndex: 5,
          }}
        >
          ∞
        </span>
      )}
      {expiryBeyondAxis && (
        <span
          data-testid={`runway-continuation-${lease.id}`}
          aria-label="Expiration continues beyond five years"
          title={`Actual expiration: ${fmtDateShort(expiryDt)}`}
          style={{
            position: "absolute",
            left: "100%",
            top: 6,
            transform: "translateX(-2px)",
            color: group.color,
            fontSize: T.fsMd,
            fontWeight: T.fwBold,
            lineHeight: 1,
            zIndex: 5,
          }}
        >
          ›
        </span>
      )}
    </div>
  );
}

// ─── Legacy track for the separate Expired footer group ───────────────────────
//
// The four active action bands use ActionRunway above. The Expired footer
// retains its existing notice/expiry phase markers because it is outside
// the active band redesign.
//
// LEASES-TIMELINE-V2-S1: a Notice date that has already passed is no longer
// drawn as a stale marker/label on the track — it renders a small "notice
// open" tag pinned at Today (0%) instead, and phase segments stop anchoring
// off it (they fall back to Expiry, same fallback hierarchy used elsewhere
// in this file).

function RowTrack({ lease, cdsForLease, group, index = 0, onNoticeLabelStagger }) {
  const today = useMemo(() => todayUtcMidnight(), []);

  // LEASES-PRECOMMENCEMENT-LABEL: detect pre-commencement (startDate in the future).
  const commenceDt = useMemo(() => {
    if (!lease.startDate) return null;
    const d = parseIso(String(lease.startDate).slice(0, 10));
    return d && d > today ? d : null;
  }, [lease.startDate, today]);

  const windowState = resolveNoticeWindowState(lease, cdsForLease, today);
  const rawNoticeDt = windowState.noticeDt;
  // A past-dated notice no longer anchors the track or draws a marker — see
  // file-header note above RowTrack. That covers both the "notice-open"
  // (expiry still ahead) and "expired" states.
  const noticePassed = windowState.state === "notice-open" || windowState.state === "expired";
  const noticeDt = noticePassed ? null : rawNoticeDt;
  // The amber "Notice open" tag only applies while the decision window is
  // still live — once expiry has also passed the lease is simply Expired,
  // not still "open" (LEASES-TIMELINE-POLISH-S3).
  const noticeOpen = windowState.state === "notice-open";
  const expiryDt = resolveExpiryDate(lease);
  const anchorDt = noticeDt || expiryDt;

  const far    = group.axisFarEdgeDays;
  const capped = !!group.axisCapped;

  const drawT = useDrawIn(index);

  const anchorDays = anchorDt ? daysBetween(today, anchorDt) : null;

  const segments = useMemo(() => {
    if (anchorDays === null) return [];
    const out = [];
    for (const seg of TIMELINE_BAR_PHASES) {
      const lo = Math.max(0, Math.min(far, anchorDays - seg.offsetLo));
      const hi = Math.max(0, Math.min(far, anchorDays - seg.offsetHi));
      if (hi > lo) {
        out.push({ key: seg.key, color: seg.color, leftPct: (lo / far) * 100, widthPct: ((hi - lo) / far) * 100 });
      }
    }
    return out;
  }, [anchorDays, far]);

  function markerPos(dt) {
    if (!dt) return null;
    const days = daysBetween(today, dt);
    const rawPct = (days / far) * 100;
    return {
      pct:     Math.max(0, Math.min(100, rawPct)),
      isCapped: capped && rawPct > 100,
    };
  }

  const noticePos    = markerPos(noticeDt);
  const expiryPos    = markerPos(expiryDt);
  const commencePos  = markerPos(commenceDt);

  // Keep nearby Notice and Expiry labels readable by measuring their text
  // bounds after inward edge alignment. Commencement labels retain the
  // existing overlap suppression behavior.
  const noticeLabelRef   = useRef(null);
  const expiryLabelRef   = useRef(null);
  const commenceLabelRef = useRef(null);
  const trackBoundsRef = useRef(null);
  const [labelAlignment, setLabelAlignment] = useState({ notice: "center", expiry: "center", commence: "center" });
  const [staggerNoticeLabel, setStaggerNoticeLabel] = useState(false);
  const [suppressCommenceLabel, setSuppressCommenceLabel] = useState(false);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const MIN_LABEL_GAP_PX = 8;

  const noticePct   = noticePos   ? noticePos.pct   : null;
  const expiryPct   = expiryPos   ? expiryPos.pct   : null;
  const commencePct = commencePos ? commencePos.pct : null;

  useLayoutEffect(() => {
    const track = trackBoundsRef.current;
    if (!track || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setLayoutRevision((revision) => revision + 1));
    observer.observe(track);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const track = trackBoundsRef.current;
    if (!track) return;
    const nextAlignment = {
      notice: noticePct === null ? "center" : measuredLabelAlignment(noticeLabelRef.current, track, noticePct),
      expiry: expiryPct === null ? "center" : measuredLabelAlignment(expiryLabelRef.current, track, expiryPct),
      commence: commencePct === null ? "center" : measuredLabelAlignment(commenceLabelRef.current, track, commencePct),
    };
    const alignmentChanged = Object.keys(nextAlignment).some((key) => nextAlignment[key] !== labelAlignment[key]);
    if (alignmentChanged) {
      setLabelAlignment(nextAlignment);
      return;
    }

    const noticeBounds = noticePct === null ? null
      : labelHorizontalBounds(noticeLabelRef.current, track, noticePct, labelAlignment.notice);
    const expiryBounds = expiryPct === null ? null
      : labelHorizontalBounds(expiryLabelRef.current, track, expiryPct, labelAlignment.expiry);
    const shouldStaggerNotice = !!noticeBounds && !!expiryBounds
      && horizontalLabelGap(noticeBounds, expiryBounds) < MIN_LABEL_GAP_PX;
    setStaggerNoticeLabel((current) => current === shouldStaggerNotice ? current : shouldStaggerNotice);

    if (commencePct === null) {
      setSuppressCommenceLabel(false);
      return;
    }
    const commenceBounds = labelHorizontalBounds(
      commenceLabelRef.current, track, commencePct, labelAlignment.commence,
    );
    const commenceConflicts = [
      expiryBounds,
      shouldStaggerNotice ? null : noticeBounds,
    ].some((bounds) => bounds && horizontalLabelGap(commenceBounds, bounds) < MIN_LABEL_GAP_PX);
    setSuppressCommenceLabel((current) => current === commenceConflicts ? current : commenceConflicts);
  }, [
    noticePct, expiryPct, commencePct, labelAlignment, staggerNoticeLabel,
    noticeDt, expiryDt, commenceDt, layoutRevision,
  ]);

  useLayoutEffect(() => {
    onNoticeLabelStagger?.(staggerNoticeLabel);
  }, [staggerNoticeLabel, onNoticeLabelStagger]);

  const noticeHoverTitle = noticePos
    ? (noticePos.isCapped ? "…capped" : `Notice ${fmtDateShort(noticeDt)}`)
    : undefined;

  return (
    <div ref={trackBoundsRef} data-testid={`timeline-track-${lease.id}`} style={{ minWidth: 0, opacity: drawT }}>
      {/* Track */}
      <div style={{ position: "relative", height: 15, display: "flex", alignItems: "center" }}>
        {/* Plain grey runway */}
        <div style={{
          position:     "absolute",
          left:         0, right: 0,
          height:       6,
          background:   T.hair,
          borderRadius: 3,
          overflow:     "hidden",
        }}>
          {segments.map((seg) => (
            <div
              key={seg.key}
              data-testid={`timeline-phase-${lease.id}-${seg.key}`}
              style={{
                position:     "absolute",
                top:          0, bottom: 0,
                left:         `${seg.leftPct}%`,
                width:        `${seg.widthPct}%`,
                background:   seg.color,
                borderRadius: 3,
              }}
            />
          ))}
        </div>

        {/* ○ Notice marker — open circle */}
        {noticePos && (
          <div
            data-testid={`notice-marker-${lease.id}`}
            title={staggerNoticeLabel ? noticeHoverTitle : undefined}
            style={{
              position:     "absolute",
              left:         `${noticePos.pct}%`,
              transform:    "translate(-50%, 0)",
              width:        10, height: 10,
              background:   T.white,
              border:       `2px solid ${T.mid}`,
              borderRadius: "50%",
              zIndex:       2,
            }}
          />
        )}

        {/* "notice open" tag — replaces a stale, past-dated notice marker
            while the decision window is still live (expiry not yet passed) */}
        {noticeOpen && (
          <span
            data-testid={`notice-open-tag-${lease.id}`}
            style={{
              position:     "absolute",
              left:         0,
              transform:    "translate(0, 0)",
              fontSize:     T.fsXxs,
              fontWeight:   T.fwBold,
              textTransform: "uppercase",
              letterSpacing: ".04em",
              padding:      "1px 6px",
              borderRadius: 8,
              background:   T.ragAmberBg,
              color:        T.ragAmber,
              whiteSpace:   "nowrap",
              zIndex:       2,
            }}
          >
            Notice open
          </span>
        )}

        {/* ◇ Commencement marker — filled diamond/circle for pre-commencement leases */}
        {commencePos && (
          <div
            data-testid={`commencement-marker-${lease.id}`}
            title={`Commences ${fmtDateShort(commenceDt)}`}
            style={{
              position:     "absolute",
              left:         `${commencePos.pct}%`,
              transform:    "translate(-50%, 0)",
              width:        10, height: 10,
              background:   "var(--v2-mid)",
              border:       `2px solid ${T.white}`,
              borderRadius: "50%",
              zIndex:       2,
            }}
          />
        )}

        {/* │ Expiry marker — vertical end-cap line */}
        {expiryPos && (
          <div
            data-testid={`expiry-marker-${lease.id}`}
            style={{
              position:     "absolute",
              left:         `${expiryPos.pct}%`,
              transform:    "translate(-50%, 0)",
              width:        3, height: 14,
              background:   T.dark,
              boxShadow:    `0 0 0 1.5px ${T.white}`,
              borderRadius: 1.5,
              zIndex:       2,
            }}
          />
        )}
      </div>

      {/* Date labels */}
      <div style={{ position: "relative", height: 12, marginTop: 3 }}>
        {expiryPos && (
          <span ref={expiryLabelRef} data-testid={`expiry-label-${lease.id}`} style={{
            position:   "absolute",
            left:       `${expiryPos.pct}%`,
            transform:  edgeLabelTransform(labelAlignment.expiry),
            fontSize:   T.fsXs,
            color:      T.light,
            whiteSpace: "nowrap",
          }}>
            {expiryPos.isCapped ? "…capped" : fmtDateShort(expiryDt)}
          </span>
        )}
        {noticePos && (
          <span
            ref={noticeLabelRef}
            data-testid={`notice-label-${lease.id}`}
            style={{
              position:   "absolute",
              left:       `${noticePos.pct}%`,
              transform:  edgeLabelTransform(labelAlignment.notice, staggerNoticeLabel ? 24 : 0),
              fontSize:   T.fsXs,
              fontWeight: T.fwSemibold,
              color:      T.dark,
              whiteSpace: "nowrap",
            }}
          >
            {noticePos.isCapped ? "…capped" : `Notice ${fmtDateShort(noticeDt)}`}
          </span>
        )}
        {/* LEASES-PRECOMMENCEMENT-LABEL: commencement date label on the axis */}
        {commencePos && (
          <span
            ref={commenceLabelRef}
            data-testid={`commencement-label-${lease.id}`}
            style={{
              position:   "absolute",
              left:       `${commencePos.pct}%`,
              transform:  edgeLabelTransform(labelAlignment.commence),
              fontSize:   T.fsXs,
              fontWeight: T.fwSemibold,
              color:      "var(--v2-mid)",
              whiteSpace: "nowrap",
              visibility: suppressCommenceLabel ? "hidden" : "visible",
            }}
          >
            {commencePos.isCapped ? "…capped" : `Commences ${fmtDateShort(commenceDt)}`}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Reduce-exposure runway ───────────────────────────────────────────────────
//
// Reduce exposure shares the same runway treatment as the other action bands.

function VacatedRowTrack({ lease, cdsForLease, group, index = 0 }) {
  return <ActionRunway lease={lease} cdsForLease={cdsForLease} group={group} index={index} />;
}

// ─── Vacated row ──────────────────────────────────────────────────────────────
//
// Single row in Reduce exposure; the sublease/winding-down context remains
// visible while the fixed Expires column always uses the actual lease date.

function fmtCarry(carryMonths) {
  if (carryMonths === null || carryMonths === undefined) return "—";
  if (carryMonths < 1) return "< 1mo";
  if (carryMonths < 12) return `${carryMonths}mo`;
  const yrs = carryMonths / 12;
  return `${yrs.toFixed(1)}yr`;
}

function VacatedTimelineRow({ lease, cdsForLease = [], group, isSelected, isFocused, onSelect, index = 0 }) {
  const [hovered, setHovered] = useState(false);
  const today = useMemo(() => todayUtcMidnight(), []);

  const posture = lease.posture || (
    lease.leaseType === "serviced_office" || lease.leaseType === "flexible"
      ? "winding-down"
      : "sublease"
  );
  const vtag = posture === "sublease" ? "Sublease" : "Winding-down";
  const primaryLine = [lease.city, lease.locationAlias].filter(Boolean).join(" · ")
    || lease.locationId || "—";

  const expiryDt = resolveExpiryDate(lease);
  const expiryCountdown = expiresInfo(lease, today);
  const windowState = resolveNoticeWindowState(lease, cdsForLease, today);

  const bg = isSelected || isFocused
    ? group.tintBg
    : hovered
      ? "var(--v2-tint)"
      : "var(--v2-card)";

  return (
    <div
      role="row"
      data-testid={`timeline-row-${lease.id}`}
      data-focus-highlight={isFocused ? "true" : undefined}
      onClick={() => onSelect(lease)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:             "grid",
        gridTemplateColumns: TIMELINE_ROW_COLUMNS,
        minWidth:            920,
        alignItems:          "center",
        gap:                 12,
        padding:             "0 16px",
        minHeight:           64,
        background:          bg,
        borderBottom:        "1px solid var(--v2-hair)",
        cursor:              "pointer",
        transition:          "background .1s",
      }}
    >
      {/* OFFICE */}
      <div style={{ overflow: "hidden" }}>
        <div
          title={primaryLine}
          style={{
            fontSize:     T.fsSm5,
            overflow:     "hidden",
            textOverflow: "ellipsis",
            whiteSpace:   "nowrap",
          }}
        >
          <OfficeNameLabel city={lease.city} alias={lease.locationAlias} locationId={lease.locationId} />
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{
            fontSize:  T.fsXs5,
            color:     "var(--v2-light)",
            whiteSpace: "nowrap",
          }}>
            {vtag}
          </span>
        </div>
        {windowState.state === "notice-open" && (
          <span
            data-testid={`notice-open-tag-${lease.id}`}
            style={{
              display: "inline-block",
              marginTop: 3,
              padding: "1px 6px",
              borderRadius: 8,
              background: T.ragAmberBg,
              color: T.ragAmber,
              fontSize: T.fsXxs,
              fontWeight: T.fwBold,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Notice open
          </span>
        )}
      </div>

      {/* Outlook is independent of timing-band membership. */}
      <div
        data-testid={`outlook-cell-${lease.id}`}
        style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
      >
        <OutlookChip lease={lease} criticalDates={cdsForLease} />
      </div>

      {/* Carry track */}
      <VacatedRowTrack lease={lease} cdsForLease={cdsForLease} group={group} index={index} />

      {/* The fixed Expires column always shows the real end date and countdown. */}
      <div style={{ textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
        <span
          data-testid={`expires-${lease.id}`}
          style={{
            display:    "block",
            fontSize:   T.fsSm5,
            fontWeight: T.fwBold,
            color:      expiryCountdown === "Expired" ? T.leaseActionBuckets["decide-now"].foreground : T.dark,
            whiteSpace: "nowrap",
          }}
        >
          {expiryDt ? fmtDateShort(expiryDt) : "—"}
        </span>
        <span
          data-testid={`expiry-countdown-${lease.id}`}
          style={{
            display: "block",
            marginTop: 1,
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            color: group.bucket === "decide-now" ? T.leaseActionBuckets["decide-now"].foreground : T.mid,
            whiteSpace: "nowrap",
          }}
        >
          {expiryCountdown}
        </span>
        {posture === "sublease" && lease.carryMonths != null && (
          <span data-testid={`carry-months-${lease.id}`} style={{ display: "block", fontSize: T.fsXxs, color: T.light }}>
            {fmtCarry(lease.carryMonths)} carry
          </span>
        )}
          {posture === "sublease" && lease.annualRentUsd != null && (
          <span
            data-testid={`carry-cost-${lease.id}`}
            style={{
              display:    "block",
              marginTop:  1,
              fontSize:   T.fsXxs,
              fontWeight: T.fwSemibold,
              color:      "var(--v2-mid)",
              whiteSpace: "nowrap",
            }}
          >
            {fmtKpiUsd(lease.annualRentUsd)}/yr
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Vacated timeline group ────────────────────────────────────────────────────
//
// Renders the combined Reduce exposure band. Header shows the at-risk annual
// rent represented by Sublease rows only; Winding-down rows carry no exposure
// total because they are already scheduled to lapse.

function VacatedTimelineGroup({ group, leases, cdByLease, selectedId, focusLeaseId, onSelect, expanded, onToggle }) {
  const totalAtRisk = sumReduceExposureAtRiskAnnualRent(leases);

  return (
    <div
      data-testid={`timeline-group-${group.bucket}`}
      style={{
        background:   "var(--v2-card)",
        borderRadius: 12,
        border:       "1px solid var(--v2-line)",
        overflow:     "visible",
        marginBottom: 10,
      }}
    >
      {/* Group header */}
      <div
        data-lease-band-header
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }}}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "var(--v2-card)",
          borderRadius: "12px 12px 0 0",
          display:     "flex",
          alignItems:  "center",
          padding:     "12px 16px",
          cursor:      "pointer",
          userSelect:  "none",
          gap:         10,
        }}
      >
        {/* Colored pill — canonical height:22/lineHeight:1/padding:0 10px spec */}
        <span style={{
          display:        "inline-flex",
          alignItems:     "center",
          justifyContent: "center",
          height:         22,
          lineHeight:     1,
          padding:        "0 10px",
          borderRadius:   6,
          fontSize:       T.fsXs,
          fontWeight:     T.fwSemibold,
          background:     group.tintBg,
          color:          group.color,
          whiteSpace:     "nowrap",
        }}>
          {group.label}
        </span>

        {/* Band context */}
        {BAND_CONTEXT[group.bucket] && (
          <span
            data-testid={`band-context-${group.bucket}`}
            style={{
              fontSize:     T.fsXs,
              color:        "var(--v2-light)",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              whiteSpace:   "nowrap",
            }}
          >
            {BAND_CONTEXT[group.bucket]}
          </span>
        )}

        {/* Lease count */}
        <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", fontWeight: T.fwSemibold }}>
          {leases.length} lease{leases.length !== 1 ? "s" : ""}
        </span>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* At-risk annual cost from Sublease rows only */}
        {group.bucket === "reduce-exposure" && totalAtRisk > 0 && (
          <span style={{ fontSize: T.fsSm5, color: group.color, fontWeight: T.fwSemibold }}>
            {fmtCostTotal(totalAtRisk)} at risk/yr
          </span>
        )}

        {/* Chevron */}
        <svg
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="var(--v2-light)" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          style={{
            transition: "transform .2s",
            transform:  expanded ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Rows */}
      {expanded && leases.length > 0 && (
        <div style={{ borderTop: "1px solid var(--v2-hair)", overflowX: "auto" }}>
          {/* Column header */}
          <div style={{
            display:             "grid",
            gridTemplateColumns: TIMELINE_ROW_COLUMNS,
            minWidth:            920,
            gap:                 12,
            padding:             "6px 16px",
            background:          "var(--v2-tint)",
            borderBottom:        "1px solid var(--v2-hair)",
          }}>
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)" }}>
              Office
            </span>
            <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)" }}>
              Outlook
            </span>
            <AxisHeader group={group} />
            <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)", textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
              Expires
            </span>
          </div>

          {leases.map((lease, i) => (
            <VacatedTimelineRow
              key={lease.id}
              lease={lease}
              cdsForLease={cdByLease[lease.id] || []}
              group={group}
              isSelected={selectedId === lease.id}
              isFocused={focusLeaseId === lease.id}
              onSelect={onSelect}
              index={i}
            />
          ))}
        </div>
      )}

      {/* Empty group state */}
      {expanded && leases.length === 0 && (
        <div style={{
          borderTop:  "1px solid var(--v2-hair)",
          padding:    "16px",
          textAlign:  "center",
          color:      "var(--v2-light)",
          fontSize:   T.fsSm,
          fontStyle:  "italic",
        }}>
          No leases in this posture
        </div>
      )}
    </div>
  );
}

// ─── Timeline row ─────────────────────────────────────────────────────────────

function TimelineRow({ lease, cdsForLease, group, isSelected, isFocused, onSelect, index = 0 }) {
  const [hovered, setHovered] = useState(false);
  const [, setNoticeLabelStaggered] = useState(false);
  const today = useMemo(() => todayUtcMidnight(), []);

  const leaseTypeLabel = lease.leaseType
    ? (LEASE_DISPLAY[lease.leaseType] ?? lease.leaseType)
    : null;
  // LEASE-GANTT-FX-FIX: use the FX-converted annualRentUsd (same field the
  // Leases list "Annual Rent" column and $/sf sort use — see Task #4231),
  // not the raw native-currency totalCost/action_value. On a mixed-currency
  // portfolio a non-USD lease's native amount is not USD and must not be
  // labeled with a "$" figure.
  const costLabel   = fmtCost(lease.annualRentUsd);
  const primaryLine = [lease.city, lease.locationAlias].filter(Boolean).join(" · ") || lease.locationId || "—";

  // LEASES-PRECOMMENCEMENT-LABEL: when the lease hasn't started yet, prepend
  // "Commences [date]" so users can see the start date at a glance in the row.
  const preCommenceLabel = useMemo(() => {
    if (!lease.startDate) return null;
    const d = parseIso(String(lease.startDate).slice(0, 10));
    if (!d || d <= today) return null;
    return `Commences ${fmtDateShort(d)}`;
  }, [lease.startDate, today]);
  const commencement = group.isIncoming ? incomingStartDate(lease) : null;
  // A commenced lease is not evidence of fit-out: require the office's
  // authoritative In-Build stage, never operational or unknown stage.
  const inFitOut = commencement && commencement <= today && lease.locationStage === "build";
  const incomingModel = group.isIncoming ? incomingRunway(lease, today) : null;

  const postureLabel = group.isIncoming ? null : lease.posture === "holdover"
    ? "Holdover"
    : lease.posture === "expired"
      ? "Closed office · no action"
      : null;
  const secondaryLine = [group.isIncoming ? null : preCommenceLabel, costLabel, leaseTypeLabel, postureLabel]
    .filter(Boolean)
    .join(" · ");
  const expires    = expiresInfo(lease, today);
  const expiryDt   = resolveExpiryDate(lease);
  const windowState = resolveNoticeWindowState(lease, cdsForLease, today);

  const bg = isSelected || isFocused
    ? group.tintBg
    : hovered
      ? "var(--v2-tint)"
      : "var(--v2-card)";

  return (
    <div
      role="row"
      data-testid={`timeline-row-${lease.id}`}
      data-focus-highlight={isFocused ? "true" : undefined}
      onClick={() => onSelect(lease)}
      tabIndex={group.isIncoming ? 0 : undefined}
      onKeyDown={group.isIncoming ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(lease);
        }
      } : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:             "grid",
        gridTemplateColumns: group.isIncoming ? INCOMING_ROW_COLUMNS : TIMELINE_ROW_COLUMNS,
        minWidth:            group.isIncoming ? 980 : 920,
        alignItems:          "center",
        gap:                 12,
        padding:             "0 16px",
        minHeight:           64,
        background:          bg,
        borderBottom:        "1px solid var(--v2-hair)",
        cursor:              "pointer",
        transition:          "background .1s",
      }}
    >
      {/* OFFICE */}
      <div style={{ overflow: "hidden" }}>
        <div
          title={primaryLine}
          style={{
            fontSize:     T.fsSm5,
            overflow:     "hidden",
            textOverflow: "ellipsis",
            whiteSpace:   "nowrap",
          }}
        >
          <OfficeNameLabel city={lease.city} alias={lease.locationAlias} locationId={lease.locationId} />
        </div>
        {secondaryLine && (
          <div style={{
            marginTop:    2,
            fontSize:     T.fsXs5,
            color:        "var(--v2-light)",
            overflow:     "hidden",
            textOverflow: "ellipsis",
            whiteSpace:   "nowrap",
          }}>
            {secondaryLine}
          </div>
        )}
        {!group.isIncoming && windowState.state === "notice-open" && (
          <span
            data-testid={`notice-open-tag-${lease.id}`}
            style={{
              display: "inline-block",
              marginTop: 3,
              padding: "1px 6px",
              borderRadius: 8,
              background: T.ragAmberBg,
              color: T.ragAmber,
              fontSize: T.fsXxs,
              fontWeight: T.fwBold,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Notice open
          </span>
        )}
      </div>

      {/* Outlook uses the same lease and critical dates as List. */}
      <div
        data-testid={`outlook-cell-${lease.id}`}
        style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
      >
        <OutlookChip lease={lease} criticalDates={cdsForLease} />
        {!group.isIncoming && lease._timelineMovedFrom && (
          <span
            data-testid={`timeline-moved-from-${lease.id}`}
            style={{
              display: "block",
              marginTop: 2,
              color: "var(--v2-accent)",
              fontSize: T.fsXxs,
              fontWeight: T.fwSemibold,
            }}
          >
            Moved from {lease._timelineMovedFrom}
          </span>
        )}
      </div>

      {group.isIncoming
        ? <>
          <IncomingRunway model={incomingModel} leaseId={lease.id} />
          <div style={{ textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
          {incomingModel.usable ? <>
            <div style={{ color: inFitOut ? T.buildBlue : T.mid, fontSize: T.fsSm5, fontWeight: T.fwSemibold }}>
              {incomingModel.future ? "Not started" : inFitOut ? "In fit-out" : null}
            </div>
            <div data-testid={`commencement-label-${lease.id}`}
              aria-label={`${incomingModel.future ? "Commences" : "Commenced"} ${commencement.toISOString().slice(0, 10)}; contractual expiry ${incomingModel.expiry.toISOString().slice(0, 10)}`}
              style={{ color: T.mid, fontSize: T.fsXs, marginTop: 2, whiteSpace: "nowrap" }}>
              {`${incomingModel.future ? "commences" : "commenced"} ${_MONTHS[commencement.getUTCMonth()]} ${commencement.getUTCFullYear()} · exp ${_MONTHS[incomingModel.expiry.getUTCMonth()]} ${incomingModel.expiry.getUTCFullYear()}`}
            </div>
          </> : <div data-testid={`commencement-label-${lease.id}`} style={{ color: T.mid, fontSize: T.fsSm5 }}>
            {commencement
              ? `${inFitOut ? "in fit-out · " : ""}commences ${_MONTHS[commencement.getUTCMonth()]} ${commencement.getUTCFullYear()}`
              : "start date TBD"}
          </div>}
          </div>
        </>
        : group.bucket === "expired"
        ? (
          <RowTrack
            lease={lease}
            cdsForLease={cdsForLease}
            group={group}
            index={index}
            onNoticeLabelStagger={setNoticeLabelStaggered}
          />
        )
        : <ActionRunway lease={lease} cdsForLease={cdsForLease} group={group} index={index} />}

      {/* Fixed Expires column: actual date stays visible even when the runway is capped. */}
      {!group.isIncoming && <div style={{ textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
        <span
          data-testid={`expires-${lease.id}`}
          style={{
            display:    "block",
            fontSize:   T.fsSm5,
            fontWeight: T.fwBold,
            color:      expires === "Expired" ? "var(--v2-red)" : T.dark,
            whiteSpace: "nowrap",
          }}
        >
          {expiryDt ? fmtDateShort(expiryDt) : "—"}
        </span>
        <span
          data-testid={`expiry-countdown-${lease.id}`}
          style={{
            display: "block",
            marginTop: 1,
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            color: group.bucket === "decide-now"
              ? T.leaseActionBuckets["decide-now"].foreground
              : expires === "Expired" ? T.leaseActionBuckets["decide-now"].foreground : T.mid,
            whiteSpace: "nowrap",
          }}
        >
          {expires}
        </span>
      </div>}
    </div>
  );
}

// ─── Accordion group ──────────────────────────────────────────────────────────

function TimelineGroup({ group, leases, cdByLease, selectedId, focusLeaseId, onSelect, expanded, onToggle }) {
  // LEASE-GANTT-FX-FIX: sum the FX-converted annualRentUsd per lease, not
  // native totalCost/action_value — on a mixed-currency group (e.g. a
  // BRL lease + a GBP lease), summing native amounts across currencies
  // produces a meaningless number even before the missing "$" conversion.
  const totalCost = leases.reduce((s, l) => s + Number(l.annualRentUsd ?? 0), 0);

  return (
    <div
      data-testid={`timeline-group-${group.bucket}`}
      style={{
        background:   "var(--v2-card)",
        borderRadius: 12,
        border:       "1px solid var(--v2-line)",
        overflow:     "visible",
        marginBottom: 10,
      }}
    >
      {/* Group header */}
      <div
        data-lease-band-header
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }}}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "var(--v2-card)",
          borderRadius: "12px 12px 0 0",
          display:        "flex",
          alignItems:     "center",
          padding:        "12px 16px",
          cursor:         "pointer",
          userSelect:     "none",
          gap:            10,
        }}
      >
        {/* Colored pill — canonical height:22/lineHeight:1/padding:0 10px spec */}
        <span style={{
          display:        "inline-flex",
          alignItems:     "center",
          justifyContent: "center",
          height:         22,
          lineHeight:     1,
          padding:        "0 10px",
          borderRadius:   6,
          fontSize:       T.fsXs,
          fontWeight:     T.fwSemibold,
          background:     group.isIncoming ? T.buildBlueBg : group.tintBg,
          color:          group.isIncoming ? T.buildBlue : group.color,
          whiteSpace:     "nowrap",
        }}>
          {group.label}
        </span>

        {/* Band context */}
        {BAND_CONTEXT[group.bucket] && (
          <span
            data-testid={`band-context-${group.bucket}`}
            style={{
              fontSize:     T.fsXs,
              color:        "var(--v2-light)",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              whiteSpace:   "nowrap",
            }}
          >
            {BAND_CONTEXT[group.bucket]}
          </span>
        )}

        {/* Lease count */}
        <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", fontWeight: T.fwSemibold }}>
          {leases.length} lease{leases.length !== 1 ? "s" : ""}
        </span>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Annual total */}
        {leases.length > 0 && (
          <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", fontWeight: T.fwSemibold }}>
            {fmtCostTotal(totalCost)}
          </span>
        )}

        {/* Chevron */}
        <svg
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="var(--v2-light)" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          style={{
            transition: "transform .2s",
            transform:  expanded ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Rows */}
      {expanded && leases.length > 0 && (
        <div style={{ borderTop: "1px solid var(--v2-hair)", overflowX: "auto" }}>
          {/* Column header */}
          <div style={{
            display:             "grid",
            gridTemplateColumns: group.isIncoming ? INCOMING_ROW_COLUMNS : TIMELINE_ROW_COLUMNS,
            minWidth:            group.isIncoming ? 980 : 920,
            gap:                 12,
            padding:             "6px 16px",
            background:          "var(--v2-tint)",
            borderBottom:        "1px solid var(--v2-hair)",
          }}>
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)" }}>
              Office
            </span>
            <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)" }}>
              Outlook
            </span>
            {group.isIncoming
              ? <div style={{ margin: "0 6px" }}><AxisHeader group={group} /></div>
              : <AxisHeader group={group} />}
            {group.isIncoming && <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: T.light, textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
              Commence / expiry
            </span>}
            {!group.isIncoming && <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--v2-light)", textAlign: "right", borderLeft: `1px solid ${T.hair}`, paddingLeft: 10 }}>
              Expires
            </span>}
          </div>

          {leases.map((lease, i) => (
            <TimelineRow
              key={lease.id}
              lease={lease}
              cdsForLease={cdByLease[lease.id] || []}
              group={group}
              isSelected={selectedId === lease.id}
              isFocused={focusLeaseId === lease.id}
              onSelect={onSelect}
              index={i}
            />
          ))}
        </div>
      )}

      {/* Empty group state */}
      {expanded && leases.length === 0 && (
        <div style={{
          borderTop:  "1px solid var(--v2-hair)",
          padding:    "16px",
          textAlign:  "center",
          color:      "var(--v2-light)",
          fontSize:   T.fsSm,
          fontStyle:  "italic",
        }}>
          {group.isIncoming ? "No incoming leases" : "No leases in this window"}
        </div>
      )}
    </div>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

function LeaseTimelineDrawer({ lease, cdByLease, open, onClose, onSelectLocation }) {
  const { flags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const marketsEnabled = flagsReady && governedNavEnabled(flags, nav_config, "plan", nav_order);
  const today = useMemo(() => todayUtcMidnight(), []);
  const cds = cdByLease[lease?.id] || [];
  const noticeDt = lease ? noticeDate(lease, cds) : null;
  const expiryDt = lease
    ? (parseIso(lease.endDate) || parseIso(lease.soonestPossibleExpiration))
    : null;

  // LEASES-TIMELINE-POLISH-S3: surface a one-line explainer when the lease
  // is in the "notice open" state (notice date passed, expiry hasn't).
  const windowState = lease ? resolveNoticeWindowState(lease, cds, today) : null;
  const decisionDeadlineDt = windowState?.periodEndDt || windowState?.expiryDt || expiryDt;

  const leaseTypeLabel = lease?.leaseType
    ? (LEASE_DISPLAY[lease.leaseType] ?? lease.leaseType)
    : null;

  const renewalCount  = Number(lease?.renewal_count) || 0;
  const renewalTerm   = Number(lease?.renewal_term_years) || 0;
  const renewalOption = renewalCount > 0 && renewalTerm > 0
    ? `${renewalCount}×${renewalTerm}yr`
    : null;

  const ownerName    = lease?.ownerName || lease?.owner || null;
  const ownerInitial = ownerName ? ownerName[0].toUpperCase() : null;

  // Group config for this lease
  const grp = TIMELINE_GROUPS.find((g) => g.bucket === lease?.action_window_bucket)
    ?? TIMELINE_GROUPS.find((g) => g.bucket === "monitor");

  return (
    <>
      {/* Drawer panel */}
      <div
        data-testid="timeline-drawer-panel"
        style={{
          position:   "fixed",
          top:        0,
          right:      0,
          height:     "100vh",
          width:      open ? 296 : 0,
          overflow:   "hidden",
          background: "var(--v2-card)",
          borderLeft: open ? "1px solid var(--v2-line)" : "none",
          boxShadow:  open ? "-4px 0 24px rgba(0,0,0,0.1)" : "none",
          zIndex:     200,
          transition: "width .22s cubic-bezier(0.4,0,0.2,1)",
          display:    "flex",
          flexDirection: "column",
        }}
      >
        {open && lease && (
          <>
          <DrawerCloseButton onClick={onClose} testId="timeline-drawer-close" />
          <div style={{ width: 296, flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <div style={{
              padding:      "18px 16px 12px",
              borderBottom: "1px solid var(--v2-hair)",
            }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 36 }}>
                <div style={{
                  fontWeight:   T.fwBold,
                  fontSize:     T.fsMd,
                  color:        "var(--v2-dark)",
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace:   "nowrap",
                  marginBottom: 3,
                }}>
                  {lease.city || lease.locationId}
                </div>
                <div style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>
                  {[lease.region, leaseTypeLabel].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
            </div>

            {/* Action pill */}
            <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--v2-hair)" }}>
              <span
                data-testid="timeline-drawer-action-pill"
                style={{
                display:      "inline-flex",
                alignItems:   "center",
                padding:      "2px 10px",
                borderRadius: 6,
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   grp.tintBg,
                color:        grp.color,
                }}
              >
                {grp.label}
              </span>
            </div>

            {/* KEY FIGURES */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-hair)" }}>
              <div style={{
                fontSize:      T.fsXxs,
                fontWeight:    T.fwBold,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color:         "var(--v2-light)",
                marginBottom:  8,
              }}>
                Key Figures
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 8px" }}>
                {[
                  // LEASE-GANTT-FX-FIX: FX-converted annualRentUsd, matching
                  // the Leases list "Annual Rent" column and the row/group
                  // labels above — not raw native totalCost/action_value.
                  { label: "Annual cost", value: fmtCost(lease.annualRentUsd) },
                  { label: "Sq ft", value: currentLeaseFootprint(lease).grossSf?.toLocaleString() ?? "—" },
                  { label: "Notice date", value: noticeDt ? fmtDateShort(noticeDt) : "—" },
                  { label: "Expiry date", value: expiryDt ? fmtDateShort(expiryDt) : "—" },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: T.fsXxs, color: "var(--v2-light)", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: "var(--v2-dark)" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RENEWAL OPTION */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-hair)" }}>
              <div style={{
                fontSize:      T.fsXxs,
                fontWeight:    T.fwBold,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color:         "var(--v2-light)",
                marginBottom:  6,
              }}>
                Renewal Option
              </div>
              {renewalOption ? (
                <span style={{
                  display:      "inline-block",
                  padding:      "3px 10px",
                  borderRadius: 6,
                  fontSize:     T.fsSm5,
                  fontWeight:   T.fwSemibold,
                  background:   "var(--v2-hair)",
                  color:        "var(--v2-mid)",
                }}>
                  {renewalOption}
                </span>
              ) : (
                <span style={{ color: "var(--v2-light)", fontSize: T.fsSm5 }}>No renewal option on file</span>
              )}
            </div>

            {/* NOTICE-OPEN EXPLAINER */}
            {windowState?.state === "notice-open" && (
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-hair)" }}>
                <div
                  data-testid="notice-open-explainer"
                  style={{
                    fontSize:     T.fsXs,
                    color:        "var(--v2-amber)",
                    lineHeight:   1.5,
                  }}
                >
                  Notice window is open (opened {fmtDateShort(windowState.noticeDt)}); decide before {fmtDateShort(decisionDeadlineDt)}.
                </div>
                {ownerName && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                    <span style={{
                      display:          "inline-flex",
                      alignItems:       "center",
                      justifyContent:   "center",
                      width:            20,
                      height:           20,
                      borderRadius:     "50%",
                      background:       "var(--v2-accent)",
                      color:            T.white,
                      fontSize:         T.fsXxs,
                      fontWeight:       T.fwBold,
                      flexShrink:       0,
                    }}>
                      {ownerInitial}
                    </span>
                    <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>{ownerName}</span>
                  </div>
                )}
              </div>
            )}

            {/* RELATED LINKS */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--v2-hair)" }}>
              <div style={{
                fontSize:      T.fsXxs,
                fontWeight:    T.fwBold,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color:         "var(--v2-light)",
                marginBottom:  8,
              }}>
                Related
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {typeof onSelectLocation === "function" && (
                  <button
                    type="button"
                    onClick={() => onSelectLocation(lease.locationId, "lease", lease.id)}
                    style={{
                      background:   "none",
                      border:       "none",
                      padding:      0,
                      cursor:       "pointer",
                      textAlign:    "left",
                      fontSize:     T.fsSm5,
                      color:        "var(--v2-accent)",
                      fontWeight:   T.fwSemibold,
                    }}
                  >
                    Open lease detail →
                  </button>
                )}
                {marketsEnabled && <span style={{ fontSize: T.fsSm5, color: "var(--v2-light)" }}>
                  View What-If scenario
                </span>}
                {marketsEnabled && <span style={{ fontSize: T.fsSm5, color: "var(--v2-light)" }}>
                  Open in Market Plan
                </span>}
              </div>
            </div>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Action buttons */}
            <div style={{ padding: "12px 16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                data-testid="drawer-log-decision"
                onClick={() => {}}
                style={{
                  width:          "100%",
                  padding:        "9px 16px",
                  borderRadius:   9,
                  background:     "var(--v2-accent)",
                  color:          T.white,
                  border:         "none",
                  fontSize:       T.fsSm5,
                  fontWeight:     T.fwBold,
                  cursor:         "pointer",
                }}
              >
                Log a decision
              </button>
              <button
                type="button"
                data-testid="drawer-mark-reviewed"
                onClick={() => {}}
                style={{
                  width:          "100%",
                  padding:        "9px 16px",
                  borderRadius:   9,
                  background:     "var(--v2-card)",
                  color:          "var(--v2-dark)",
                  border:         "1px solid var(--v2-line)",
                  fontSize:       T.fsSm5,
                  fontWeight:     T.fwBold,
                  cursor:         "pointer",
                }}
              >
                Mark as reviewed
              </button>
            </div>
          </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

// Props:
//   leases           — derived (from deriveLeaseActions), already search+filter applied
//   cdByLease        — { [leaseId]: CriticalDateRow[] }
//   onSelectLocation — (locationId, tab?, leaseId?) => void
//   expandedGroups   — { [bucket]: boolean } — controlled from parent
//   onGroupToggle    — (bucket: string) => void
//   onSelectLease    — (lease|null) => void  — when provided, selection is lifted to parent
//   selectedLeaseId  — string|null           — controlled selected ID from parent
export function LeasesTimelineView({
  leases,
  cdByLease,
  onSelectLocation,
  expandedGroups,
  onGroupToggle,
  onSelectLease,
  selectedLeaseId,
  focusLeaseId,
  onOrderedIdsChange,
}) {
  const [selectedLease, setSelectedLease] = useState(null);
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const today = useMemo(() => todayUtcMidnight(), []);

  // When filters change, clear selection if it's no longer visible.
  const leaseIds = useMemo(() => new Set(leases.map((l) => l.id)), [leases]);
  useEffect(() => {
    if (selectedLease && !leaseIds.has(selectedLease.id)) {
      setSelectedLease(null);
      setDrawerOpen(false);
    }
  }, [leaseIds, selectedLease]);

  const handleSelect = useCallback((lease) => {
    if (onSelectLease) {
      // Delegate to parent (LeasesPage SlideSheet); toggle off if same lease.
      onSelectLease(selectedLeaseId === lease.id ? null : lease);
    } else {
      if (selectedLease?.id === lease.id) {
        setSelectedLease(null);
        setDrawerOpen(false);
      } else {
        setSelectedLease(lease);
        setDrawerOpen(true);
      }
    }
  }, [onSelectLease, selectedLeaseId, selectedLease]);

  const handleCloseDrawer = useCallback(() => {
    setSelectedLease(null);
    setDrawerOpen(false);
  }, []);

  // The active selected ID: prefer parent-controlled when onSelectLease is wired.
  const effectiveSelectedId = onSelectLease
    ? (selectedLeaseId ?? null)
    : (selectedLease?.id ?? null);

  // Committed is a display-only lane; all other bands retain their canonical
  // buckets and act-by ordering. Incoming uses sourced commencement instead.
  const grouped = useMemo(() => {
    const byBucket = {};
    for (const g of TIMELINE_GROUPS) byBucket[g.bucket] = [];
    for (const l of leases) {
      const bucket = timelineLaneFor(l, today);
      if (byBucket[bucket] !== undefined) byBucket[bucket].push(l);
      else byBucket["monitor"].push(l);
    }

    // Sort active bands by days_to_next_action ascending (soonest first).
    const activeCmp = (a, b) => {
      const da = a.days_to_next_action;
      const db = b.days_to_next_action;
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    };
    for (const g of TIMELINE_GROUPS) {
      byBucket[g.bucket].sort(g.isIncoming ? compareIncomingStarts : activeCmp);
    }
    return byBucket;
  }, [leases, today]);

  const displayGroups = useMemo(() => {
    const reduceExpiries = (grouped["reduce-exposure"] || [])
      .map(resolveExpiryDate)
      .filter(Boolean);
    const reduceFarEdge = Math.max(
      1,
      ...reduceExpiries.map((expiryDt) => Math.max(1, daysBetween(today, expiryDt))),
    );
    const reduceTicks = [0.25, 0.5, 0.75, 1]
      .map((fraction) => {
        const days = Math.max(1, Math.round(reduceFarEdge * fraction));
        const months = Math.max(1, Math.round(days / 30.4375));
        const label = months < 12
          ? `${months}mo`
          : `${Number((months / 12).toFixed(months % 12 === 0 ? 0 : 1))}y`;
        return { days, label };
      })
      .filter((tick, index, ticks) => ticks.findIndex((candidate) => candidate.days === tick.days) === index);

    return TIMELINE_GROUPS.map((group) => group.bucket === "reduce-exposure"
      ? { ...group, axisFarEdgeDays: reduceFarEdge, axisTicks: reduceTicks }
      : group);
  }, [grouped, today]);

  // Report the band-ordered ID list to the parent so it can pass the correct
  // orderedIds to the drawer (enabling Prev/Next paging in band order).
  useEffect(() => {
    if (typeof onOrderedIdsChange !== "function") return;
    const ids = TIMELINE_GROUPS.flatMap((g) => (grouped[g.bucket] || []).map((l) => l.id));
    onOrderedIdsChange(ids);
  }, [grouped, onOrderedIdsChange]);

  return (
    <div style={{ position: "relative" }}>
      {/* Visibility follows the filtered leases, independently of expansion preferences. */}
      {displayGroups.filter((group) => grouped[group.bucket].length > 0).map((group) =>
        group.isVacated ? (
          <VacatedTimelineGroup
            key={group.bucket}
            group={group}
            leases={grouped[group.bucket] || []}
            cdByLease={cdByLease}
            selectedId={effectiveSelectedId}
            focusLeaseId={focusLeaseId}
            onSelect={handleSelect}
            expanded={expandedGroups[group.bucket] ?? group.expandedByDefault}
            onToggle={() => onGroupToggle(group.bucket)}
          />
        ) : (
          <TimelineGroup
            key={group.bucket}
            group={group}
            leases={grouped[group.bucket] || []}
            cdByLease={cdByLease}
            selectedId={effectiveSelectedId}
            focusLeaseId={focusLeaseId}
            onSelect={handleSelect}
            expanded={expandedGroups[group.bucket] ?? group.expandedByDefault}
            onToggle={() => onGroupToggle(group.bucket)}
          />
        )
      )}

      {/* Internal drawer — only used when selection is NOT delegated to the parent.
          When onSelectLease is wired, the parent SlideSheet (LeaseDrawerV3) handles it. */}
      {!onSelectLease && (
        <LeaseTimelineDrawer
          lease={selectedLease}
          cdByLease={cdByLease}
          open={drawerOpen}
          onClose={handleCloseDrawer}
          onSelectLocation={onSelectLocation}
        />
      )}
    </div>
  );
}
